import { ECLIPSE_LIGHT_PRODUCT_FAMILY_ID, ECLIPSE_CORE_PRODUCT_FAMILY_ID, ECLIPSE_BUSINESS_PRODUCT_FAMILY_ID, ECLIPSE_BUSINESS_ELITE_PRODUCT_FAMILY_ID } from '@wix/package-picker-shared';
import { useCurrentModule } from '../components';
import { useEclipseAiContent } from './useEclipseAiContent';
import { usePersonalizedDescriptions } from './usePersonalizedDescriptions';
const ECLIPSE_TIER_BY_PRODUCT_FAMILY = {
  [ECLIPSE_BUSINESS_ELITE_PRODUCT_FAMILY_ID]: 'business_elite',
  [ECLIPSE_BUSINESS_PRODUCT_FAMILY_ID]: 'business',
  [ECLIPSE_CORE_PRODUCT_FAMILY_ID]: 'core',
  [ECLIPSE_LIGHT_PRODUCT_FAMILY_ID]: 'light'
};
const TIERING_DESCRIPTION_KEY_BY_PRODUCT_FAMILY = {
  [ECLIPSE_BUSINESS_ELITE_PRODUCT_FAMILY_ID]: 'premium-purchase-plan.package_picker.plan_subtitle.business_elite',
  [ECLIPSE_BUSINESS_PRODUCT_FAMILY_ID]: 'premium-purchase-plan.package_picker.plan_subtitle.business',
  [ECLIPSE_CORE_PRODUCT_FAMILY_ID]: 'premium-purchase-plan.package_picker.plan_subtitle.core',
  [ECLIPSE_LIGHT_PRODUCT_FAMILY_ID]: 'premium-purchase-plan.package_picker.plan_subtitle.light'
};
const SERVICES_DESCRIPTION_KEY_BY_PRODUCT_FAMILY = {
  [ECLIPSE_BUSINESS_ELITE_PRODUCT_FAMILY_ID]: 'premium-purchase-plan.cards.business_elite.subtitle.services',
  [ECLIPSE_BUSINESS_PRODUCT_FAMILY_ID]: 'premium-purchase-plan.business.subtitle.services',
  [ECLIPSE_CORE_PRODUCT_FAMILY_ID]: 'premium-purchase-plan.core.subtitle.services',
  [ECLIPSE_LIGHT_PRODUCT_FAMILY_ID]: 'premium-purchase-plan.light.subtitle.services'
};
/**
 * Returns the description to render for a product card. Resolves, in
 * precedence order, to:
 * - `undefined` while any source that could win is still in flight.
 *   Consumers should render a skeleton in this case — the prefetches in
 *   the module Provider keep this window very short.
 * - The personalized translation when `eclipsePersonalizedContent` is on
 *   and the server returned an item for this product family.
 * - The AI plan copy for the card's tier when `eclipseAiPlanCopy` is on
 *   and the model emitted a description for that tier.
 * - The dealer translation when the `useDealerDescriptions` experiment
 *   is on and the dealer endpoint returned a `translationKey`. Dealer
 *   being on also suppresses the legacy overrides below.
 * - The plan description from the product fetch otherwise (with
 *   optional override from `tieringContentChanges` /
 *   `bookingsPersonalization` when dealer is off).
 *
 * Reads the raw plan description from `useFetchProduct({ productId })`
 * internally — react-query dedupes against the call sites that already
 * fetched the same product, so no extra request fires.
 */
export const useProductDescription = _ref => {
  var _offering$products;
  let {
    productId
  } = _ref;
  const module = useCurrentModule();
  const {
    t
  } = module.internals.useTranslations();
  const {
    experiments
  } = module.internals.experiments.hooks.useExperiments();
  const isUseDealerDescriptions = experiments.isEnabled('specs.premium.useDealerDescriptions');
  const isBookingsPersonalization = experiments.isEnabled('specs.premium.bookingsPersonalization');
  const isTieringContentChanges = experiments.isEnabled('specs.premium.tieringContentChanges');
  const {
    data: offering
  } = module.SDK.eclipseOfferingApiClient.hooks.useFetchOffering({});
  const {
    data: product
  } = module.SDK.eclipseOfferingApiClient.hooks.useFetchProduct({
    productId
  });
  // Only fire the dealer-descriptions query for users in the experiment, so
  // the control group doesn't hit the new endpoint. Deduped against the
  // prefetch mounted in the module Provider, so the request fires once at
  // most per session.
  const {
    data: productDescription,
    isLoading: isProductDescriptionLoading
  } = module.SDK.eclipseOfferingApiClient.hooks.useFetchProductDescription({}, isUseDealerDescriptions);
  const {
    getDescriptionTranslation: getPersonalizedDescriptionTranslation,
    isLoading: isPersonalizedLoading
  } = usePersonalizedDescriptions();
  const {
    getTierDescription: getAiTierDescription,
    isLoading: isAiLoading
  } = useEclipseAiContent();
  const productFamilyId = offering == null || (_offering$products = offering.products) == null || (_offering$products = _offering$products.find(p => p.productId === productId)) == null ? void 0 : _offering$products.productFamilyId;

  // Resolve the card's tier from its product family, then look up the AI
  // content by that slug — the model never emits product IDs. An unmapped
  // family (or a tier the model omitted) yields no AI content and falls
  // through to Dealer (tier-3) below.
  const tier = productFamilyId ? ECLIPSE_TIER_BY_PRODUCT_FAMILY[productFamilyId] : undefined;
  const aiDescription = tier ? getAiTierDescription(tier) : undefined;

  // Served as a Babel key matched on productFamilyId, so translate it here.
  const personalizedDescriptionTranslation = getPersonalizedDescriptionTranslation(productFamilyId);
  const rawDescription = product == null ? void 0 : product.description;
  const isLoading = isAiLoading || isPersonalizedLoading || isUseDealerDescriptions && isProductDescriptionLoading;
  if (isLoading) {
    return {
      description: undefined,
      isLoading: true
    };
  }
  if (personalizedDescriptionTranslation) {
    return {
      description: t(personalizedDescriptionTranslation.key, personalizedDescriptionTranslation.params),
      isLoading: false
    };
  }
  if (aiDescription) {
    return {
      description: aiDescription,
      isLoading: false
    };
  }

  // Dealer takes over the entire description override flow when on. The
  // legacy experiment-based overrides (bookings personalization / tiering
  // content) are intentionally skipped — dealer is the new way to customize
  // and we don't want both systems competing.
  if (isUseDealerDescriptions) {
    var _productDescription$d;
    const dealerTranslationKey = productFamilyId ? productDescription == null || (_productDescription$d = productDescription.descriptions) == null || (_productDescription$d = _productDescription$d.find(item => item.productFamilyId === productFamilyId)) == null || (_productDescription$d = _productDescription$d.description) == null ? void 0 : _productDescription$d.translationKey : undefined;
    if (dealerTranslationKey) {
      return {
        description: t(dealerTranslationKey),
        isLoading: false
      };
    }
    return {
      description: rawDescription,
      isLoading: false
    };
  }
  if (!isBookingsPersonalization && !isTieringContentChanges) {
    return {
      description: rawDescription,
      isLoading: false
    };
  }
  const keyMap = isBookingsPersonalization ? SERVICES_DESCRIPTION_KEY_BY_PRODUCT_FAMILY : TIERING_DESCRIPTION_KEY_BY_PRODUCT_FAMILY;
  const descriptionKey = productFamilyId ? keyMap[productFamilyId] : undefined;
  if (descriptionKey) {
    return {
      description: t(descriptionKey),
      isLoading: false
    };
  }
  return {
    description: rawDescription,
    isLoading: false
  };
};
//# sourceMappingURL=useProductDescription.js.map